import React from 'react';

import {assert} from 'Asserts';
import {
  FieldsChainField,
  generateUniqueFieldId,
  getFieldsChainFromLeafToRoot,
  getNestedSchemaFieldsForTopLevelSchema,
} from 'notebook/tables/lookup-field/LookupConfigurationUtil';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';

export interface NestedSchemaFieldSelectorProps {
  selectedFieldsChain: Array<FieldsChainField>;
  onSelectedFieldChainChange: Function;
  topLevelSchema: TagSchemaSummaryWithFieldsGraphQL;

  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}

/**
 * A tree that renders the hierarchy of link fields (batch and entity links) with the tag schema as
 * the tree node.
 */
export class NestedSchemaFieldSelector extends React.Component<NestedSchemaFieldSelectorProps> {
  _getFields(): Array<FieldsChainField> {
    return getNestedSchemaFieldsForTopLevelSchema({
      tagSchema: this.props.topLevelSchema,
      tagSchemasById: this.props.tagSchemasById,
    });
  }

  _getRootNodeId(): string {
    return generateUniqueFieldId({parentFieldId: null, id: this.props.topLevelSchema.id});
  }

  _getTreeItems(fields: Array<FieldsChainField>): Array<TreeItem> {
    const rootNodeId = this._getRootNodeId();
    const treeItems: Array<TreeItem> = [
      {
        id: rootNodeId,
        forceToTop: true,
        icon: 'field',
        name: this.props.topLevelSchema.name,
        parentId: null,
        // Start with the top-level schema field expanded.
        isInitiallyExpanded: true,
      },
      ...fields.map((field): TreeItem => ({
        id: field.uniqueFieldId,
        forceToTop: false,
        icon: 'field',
        name: field.name,
        parentId: field.parentFieldId == null ? rootNodeId : field.parentFieldId,
        isInitiallyExpanded: false,
      })),
    ];
    return treeItems;
  }

  _getSelectedFieldId(): string | null {
    if (this.props.selectedFieldsChain.length === 0) {
      return null;
    }
    return this.props.selectedFieldsChain[0].uniqueFieldId;
  }

  _handleChangeSelectedFields = (uniqueFieldIds: Array<string>): void => {
    assert(uniqueFieldIds.length <= 1, 'Expected 1 or fewer fields');
    // If the root node is selected in the tree - this is the schema and therefore it doesn't need to be
    // included in the fields chain.
    const uniqueFieldId =
      uniqueFieldIds.length === 0 || uniqueFieldIds[0] === this._getRootNodeId() ? null : uniqueFieldIds[0];
    const allFields = this._getFields();
    this.props.onSelectedFieldChainChange(getFieldsChainFromLeafToRoot({allFields, uniqueFieldId}));
  };

  override render(): JSX.Element | null {
    const fields = this._getFields();
    if (fields.length === 0) {
      return null;
    }
    const selectedFieldId = this._getSelectedFieldId();
    // TODO(BNCH-7208) Remove the key field below and fix the state ofthe SelectTree
    // We need to use the key as the SelectTree doesn't support
    // changing the root of the tree which is what we are doing when
    // we swap the topLevelSchema.  By using the key, we are telling
    // React to re-create the SelectTree anytime the key changes.
    // This is normally used for subitems in a list so React can know
    // when items are removed or reordered but we can use it here to
    // give React the hint that it should throw away the old
    // SelectTree.
    return (
      <FormGroup>
        <Label>Sample to lookup on</Label>
        <SelectTree
          key={`${this._getRootNodeId()}`}
          allowNone={false}
          items={this._getTreeItems(fields)}
          multiple={false}
          selectedIds={
            selectedFieldId == null
              ? // Select the tag schema node by detault
                [generateUniqueFieldId({parentFieldId: null, id: this.props.topLevelSchema.id})]
              : [selectedFieldId]
          }
          onChangeSelectedIds={this._handleChangeSelectedFields}
        />
      </FormGroup>
    );
  }
}

export default NestedSchemaFieldSelector;
